import React, { FC, forwardRef } from 'react'

import { twMerge } from 'tailwind-merge'
import { tv } from 'tailwind-variants'

import useFavorite from '@/hooks/useFavorite'
import { Icon, type IconProps } from '@components/Icon'

interface MenuItemProps {
  item: {
    iconName?: string
    label: string
    url?: string
    subItems?: any[]
    isFavorite?: boolean
    favoritesCount?: number
    isInstantSearch?: boolean
  }
  onClick?: () => void
  isMultiColumn?: boolean
  isDirectLink?: boolean
  isActive?: boolean
  isSecondary?: boolean
  className?: string
}

const menuItemStyles = tv({
  base: 'flex items-center gap-x-3 py-2 w-full rounded transition sidebar-elmt hover:opacity-100 cursor-pointer text-left',
  variants: {
    hasSubItems: {
      true: 'relative origin-left transition-all duration-200 ease-out',
      false: '',
    },
    isActive: {
      true: 'text-white',
      false: '',
    },
    isMultiColumn: {
      true: '',
      false: '',
    },
    isDirectLink: {
      true: '',
      false: '',
    },
    isSecondary: {
      true: '',
      false: '',
    },
    isFavorite: {
      true: 'gap-x-2',
      false: '',
    },
  },
  compoundVariants: [
    {
      isActive: true,
      isMultiColumn: false,
      className:
        'text-sm underline underline-offset-4 text-black transition-colors duration-[400ms]',
    },
    {
      isMultiColumn: true,
      isActive: true,
      isSecondary: false,
      className: 'font-bold text-black',
    },
    {
      isActive: false,
      isMultiColumn: true,
      className: 'text-black',
    },
    {
      isActive: false,
      isMultiColumn: false,
      className: 'transition-colors duration-[400ms]',
    },
  ],
})

const renderMenuItemContent = (item: MenuItemProps['item']) => (
  <>
    <span>{item.label}</span>
    {item.iconName && <Icon name={item.iconName} size={'14'} />}
    {item.isFavorite && (
      <span className="relative no-underline">
        <Icon name="heart_dark" className="fill-none" size={16} />
        {item.favoritesCount && item.favoritesCount > 0 ? (
          <span className="absolute ltr:right-[-10px] rtl:left-[-10px] top-1/2 flex size-[14px] items-center justify-center rounded-full bg-red-monaco text-[10px] text-white leading-none -translate-y-1/2">
            {item.favoritesCount}
          </span>
        ) : null}
      </span>
    )}
  </>
)

const MenuItem = forwardRef<
  HTMLAnchorElement | HTMLButtonElement,
  MenuItemProps
>(
  (
    {
      item,
      onClick,
      isMultiColumn,
      isActive = false,
      isSecondary = false,
      className,
    },
    ref,
  ) => {
    const isDirectLink = !item.subItems || item.subItems.length === 0
    const hasSubItems = !!item.subItems

    if (item.isFavorite) {
      const { countFavorites } = useFavorite()

      item.favoritesCount = countFavorites()
    }

    const classes = twMerge(
      className,
      menuItemStyles({
        isMultiColumn,
        isDirectLink,
        isActive,
        isSecondary,
        isFavorite: item.isFavorite || false,
        hasSubItems,
      }),
      isActive && (isMultiColumn || isSecondary) && 'font-bold',
    )

    if (item.isInstantSearch && onClick && item.url) {
      return (
        <a
          ref={ref as React.LegacyRef<HTMLAnchorElement>}
          href={item.url}
          onClick={(e) => {
            e.preventDefault()
            onClick()
          }}
          className={classes}
          aria-label={item.label}
          data-text={item.label}
        >
          {renderMenuItemContent(item)}
        </a>
      )
    }

    return isDirectLink ? (
      <a
        ref={ref as React.LegacyRef<HTMLAnchorElement>}
        href={item.url}
        className={classes}
        aria-label={item.label}
        data-text={item.label}
      >
        {renderMenuItemContent(item)}
      </a>
    ) : (
      <button
        ref={ref as React.LegacyRef<HTMLButtonElement>}
        onClick={onClick}
        className={classes}
        aria-expanded={isActive}
        aria-controls={`submenu-${item.label}`}
        data-text={item.label}
      >
        {renderMenuItemContent(item)}
      </button>
    )
  },
)

export default MenuItem
